import {
  Table,
  Button,
  Space,
  Card,
  Input,
  Tag,
  Popconfirm,
  Typography,
  Row,
  Col,
  Select,
  Slider,
  InputNumber,
  Divider,
  Collapse
} from 'antd'
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  FilterOutlined
} from '@ant-design/icons'
import React, { useState } from 'react'
import { GlobalServicesEnums } from '../utils/enums'

const { Search } = Input
const { Title } = Typography
const { Option } = Select
const { Panel } = Collapse

const GlobalServicesTable = ({
  services,
  editRecord,
  addRecord,
  deleteRecord,
  refreshView,
  loading,
  debouncedGetServices,
  handleAddFilterChange
}) => {
  const [searchValue, setSearchValue] = useState('')
  const [filtersVisible, setFiltersVisible] = useState(false)
  const [selectedCategories, setSelectedCategories] = useState([])
  const [rateRange, setRateRange] = useState([0, 10000])
  const [durationFilter, setDurationFilter] = useState(null)

  const handleEdit = (record) => {
    editRecord(record)
  }

  const handleDelete = async (recordId) => {
    await deleteRecord(recordId)
  }

  const getCategoryLabel = (categoryValue) => {
    const category = GlobalServicesEnums.categories.find((cat) => cat.value === categoryValue)
    return category ? category.label : categoryValue
  }

  // Filter handlers
  const handleCategoryFilterChange = (selectedValues) => {
    setSelectedCategories(selectedValues)
    selectedValues.forEach((value) => {
      handleAddFilterChange('category', value)
    })
  }

  const handleRateFilterChange = (range) => {
    setRateRange(range)
    handleAddFilterChange('rate', range)
  }

  const handleDurationFilterChange = (value) => {
    setDurationFilter(value)
    handleAddFilterChange('duration', value)
  }

  const clearAllFilters = () => {
    setSelectedCategories([])
    setRateRange([0, 0])
    setDurationFilter(null)
    // Reset filters in parent component
    handleAddFilterChange('category', [])
    handleAddFilterChange('rate', [0, 0])
    handleAddFilterChange('duration', null)
  }

  const columns = [
    {
      title: 'Service Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder='Search name'
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.name.toString().toLowerCase().includes(value.toLowerCase()),
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => (
        <Typography.Paragraph ellipsis={{ rows: 2, expandable: true, symbol: 'more' }} style={{ margin: 0 }}>
          {text}
        </Typography.Paragraph>
      )
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      sorter: (a, b) => getCategoryLabel(a.category).localeCompare(getCategoryLabel(b.category)),
      filters: GlobalServicesEnums.categories.map((cat) => ({
        text: cat.label,
        value: cat.value
      })),
      onFilter: (value, record) => record.category === value,
      render: (categoryValue) => <Tag color='blue'>{getCategoryLabel(categoryValue)}</Tag>
    },
    {
      title: 'Default Rate',
      dataIndex: 'defaultRate',
      key: 'defaultRate',
      sorter: (a, b) => a.defaultRate - b.defaultRate,
      render: (rate) => (
        <Space>
          <DollarOutlined style={{ color: '#52c41a' }} />
          <span style={{ fontWeight: 'bold', color: '#52c41a' }}>R{Number(rate).toFixed(2)}</span>
        </Space>
      )
    },
    {
      title: 'Duration',
      dataIndex: 'defaultDurationMin',
      key: 'defaultDurationMin',
      sorter: (a, b) => a.defaultDurationMin - b.defaultDurationMin,
      render: (duration) => (
        <Space>
          <ClockCircleOutlined style={{ color: '#1890ff' }} />
          <span>{duration} min</span>
        </Space>
      )
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false }
      ],
      onFilter: (value, record) => record.active === value,
      render: (active) => <Tag color={active ? 'success' : 'default'}>{active ? 'Active' : 'Inactive'}</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size='small'>
          <Button
            type='primary'
            size='small'
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title='Edit Service'
          >
            Edit
          </Button>
          <Popconfirm
            title='Delete Service'
            description='Are you sure you want to delete this service?'
            onConfirm={() => handleDelete(record._id)}
            okText='Yes'
            cancelText='No'
            okButtonProps={{ danger: true }}
          >
            <Button danger size='small' icon={<DeleteOutlined />} title='Delete Service'>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <Card>
      <Row justify='space-between' align='middle' style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Global Services Management
          </Title>
        </Col>
        <Col>
          <Space>
            <Input
              placeholder='Search services...'
              allowClear
              size='large'
              onChange={(e) => debouncedGetServices(e.target.value)}
              style={{ width: 300 }}
            />
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFiltersVisible(!filtersVisible)}
              type={filtersVisible ? 'primary' : 'default'}
            >
              Advanced Filters
            </Button>
            <Button type='primary' icon={<PlusOutlined />} size='large' onClick={addRecord}>
              Add Service
            </Button>
          </Space>
        </Col>
      </Row>

      {filtersVisible && (
        <Card size='small' style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <div style={{ marginBottom: 8 }}>
                <strong>Filter by Categories:</strong>
              </div>
              <Select
                mode='multiple'
                placeholder='Select categories'
                value={selectedCategories}
                onChange={handleCategoryFilterChange}
                style={{ width: '100%' }}
                allowClear
              >
                {GlobalServicesEnums.categories.map((category) => (
                  <Option key={category.value} value={category.value}>
                    {category.label}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col span={8}>
              <div style={{ marginBottom: 8 }}>
                <strong>Filter by Rate Range (R):</strong>
              </div>
              <Row gutter={8}>
                <Col span={12}>
                  <InputNumber
                    placeholder='Min'
                    value={rateRange[0]}
                    onChange={(value) => handleRateFilterChange([value || 0, rateRange[1]])}
                    style={{ width: '100%' }}
                    min={0}
                    formatter={(value) => `R ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value) => value.replace(/R\s?|(,*)/g, '')}
                  />
                </Col>
                <Col span={12}>
                  <InputNumber
                    placeholder='Max'
                    value={rateRange[1]}
                    onChange={(value) => handleRateFilterChange([rateRange[0], value || 10000])}
                    style={{ width: '100%' }}
                    min={0}
                    formatter={(value) => `R ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value) => value.replace(/R\s?|(,*)/g, '')}
                  />
                </Col>
              </Row>
              <Slider
                range
                value={rateRange}
                onChange={handleRateFilterChange}
                max={10000}
                step={50}
                style={{ marginTop: 8 }}
                tooltip={{
                  formatter: (value) => `R ${value}`
                }}
              />
            </Col>

            <Col span={8}>
              <div style={{ marginBottom: 8 }}>
                <strong>Filter by Duration (minutes):</strong>
              </div>
              <Select
                placeholder='Select duration'
                value={durationFilter}
                onChange={handleDurationFilterChange}
                style={{ width: '100%' }}
                allowClear
              >
                <Option value={15}>15 minutes</Option>
                <Option value={30}>30 minutes</Option>
                <Option value={45}>45 minutes</Option>
                <Option value={60}>1 hour</Option>
                <Option value={90}>1.5 hours</Option>
                <Option value={120}>2 hours</Option>
                <Option value={180}>3 hours</Option>
              </Select>
            </Col>
          </Row>

          <Divider />

          <Row justify='end'>
            <Col>
              <Space>
                <Button onClick={clearAllFilters}>Clear All Filters</Button>
                <Button type='primary' onClick={refreshView} loading={loading}>
                  Apply Filters
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      <Table
        columns={columns}
        dataSource={services}
        rowKey='_id'
        loading={loading}
        pagination={{
          total: services?.length || 0,
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} services`,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
        scroll={{ x: 1200 }}
        size='middle'
        bordered
        title={() => (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              <strong>Total Services: </strong>
              <Tag color='blue'>{services?.length || 0}</Tag>
              {(selectedCategories.length > 0 || durationFilter || rateRange[0] > 0 || rateRange[1] < 10000) && (
                <Tag color='orange' style={{ marginLeft: 8 }}>
                  Filters Applied
                </Tag>
              )}
            </span>
            <Button onClick={refreshView} loading={loading} icon={<SearchOutlined />}>
              Refresh
            </Button>
          </div>
        )}
      />
    </Card>
  )
}

export default GlobalServicesTable
